const normalizeBarcode = (value) => String(value || "").trim().toLowerCase();

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const sameStock = (item, medicine) => {
  if (!item || !medicine) return false;

  const itemBarcode = normalizeBarcode(item.barcode);
  const medicineBarcode = normalizeBarcode(medicine.barcode);
  if (itemBarcode && medicineBarcode && itemBarcode === medicineBarcode) {
    return true;
  }

  const stockId = medicine.id ?? medicine.stock_medicine;
  if (item.stock_medicine != null && stockId != null) {
    if (String(item.stock_medicine) === String(stockId)) return true;
  }

  const medicineId = medicine.medicine ?? medicine.medicine_id;
  if (medicineId != null) {
    if (item.medicine != null && String(item.medicine) === String(medicineId)) {
      return true;
    }
    if (
      item.stock_medicine != null &&
      String(item.stock_medicine) === String(medicineId)
    ) {
      return true;
    }
  }

  return false;
};

export const getTotalAvailableStock = (stockList, barcode, fallbackMedicine) => {
  const code = normalizeBarcode(barcode || fallbackMedicine?.barcode);
  const list = asList(stockList);
  if (code && list.length) {
    const today = new Date().toISOString().split("T")[0];
    const total = list
      .filter((item) => {
        if (normalizeBarcode(item.barcode) !== code) return false;
        if (Number(item.quantity) <= 0) return false;
        if (item.expiry_date && String(item.expiry_date) < today) return false;
        return true;
      })
      .reduce((sum, item) => sum + Number(item.quantity), 0);
    if (total > 0) return total;
  }
  return (
    Number(fallbackMedicine?.available_stock) ||
    Number(fallbackMedicine?.quantity) ||
    0
  );
};

export const buildStockScanPayload = (bestStock, barcode, stockList) => {
  const available = getTotalAvailableStock(stockList, barcode, bestStock);
  return {
    ...bestStock,
    barcode: bestStock?.barcode || barcode,
    quantity: available,
    available_stock: available,
  };
};

export const getStockCartState = (items, medicine) => {
  const existingIndex = items.findIndex((item) => sameStock(item, medicine));
  const available =
    Number(medicine.available_stock) ||
    Number(medicine.quantity) ||
    Number(items[existingIndex]?.available_stock) ||
    0;
  const currentQty = items.reduce(
    (sum, item) =>
      sameStock(item, medicine) ? sum + (parseFloat(item.quantity) || 0) : sum,
    0,
  );
  return { existingIndex, available, currentQty };
};

export const canIncrementStockItem = (items, medicine, quantity = 1) => {
  if (!medicine) {
    return { ok: false, reason: "invalid", available: 0, currentQty: 0 };
  }
  const qtyToAdd = Number(quantity) || 1;
  const { available, currentQty } = getStockCartState(items, medicine);

  if (available <= 0) {
    return { ok: false, reason: "out_of_stock", available, currentQty };
  }
  if (currentQty + qtyToAdd > available) {
    return {
      ok: false,
      reason: "exceeds_stock",
      available,
      currentQty,
    };
  }
  return { ok: true, available, currentQty };
};

export const tryAddStockScan = (items, medicine, quantity = 1) => {
  const check = canIncrementStockItem(items, medicine, quantity);
  if (!check.ok) {
    return { ok: false, check, items };
  }
  return {
    ok: true,
    check,
    items: addOrIncrementStockItem(items, medicine, quantity),
  };
};

export const stockScanErrorMessage = (check, medicineName, t) => {
  if (check.reason === "exceeds_stock") {
    return t("pharmacy.barcodeNoMoreQty", {
      name: medicineName,
      available: check.available,
    });
  }
  if (check.reason === "out_of_stock") {
    return t("pharmacy.barcodeOutOfStock", { name: medicineName });
  }
  return t("pharmacy.barcodeOutOfStock", { name: medicineName });
};

export const showStockQtyAlert = (check, medicineName, t, toast) => {
  const message = stockScanErrorMessage(check, medicineName, t);
  if (toast) {
    toast.error(message, { autoClose: 6000 });
  }
  window.alert(message);
};

export const createScanQueue = () => {
  let tail = Promise.resolve();
  return (task) => {
    const next = tail.then(task).catch(() => {});
    tail = next;
    return next;
  };
};

const cappedQty = (nextQty, available) => {
  const qty = Math.max(0, Number(nextQty) || 0);
  const max = Number(available);
  if (!Number.isFinite(max) || max <= 0) return qty;
  return Math.min(qty, max);
};

export const addOrIncrementStockItem = (items, medicine, quantity = 1) => {
  if (!medicine) return items;
  const qtyToAdd = Number(quantity) || 1;
  const existingIndex = items.findIndex((item) => sameStock(item, medicine));
  const available =
    Number(medicine.available_stock) ||
    Number(medicine.quantity) ||
    Number(items[existingIndex]?.available_stock) ||
    0;

  if (existingIndex >= 0) {
    return items.map((item, index) => {
      if (index !== existingIndex) return item;
      const sellPrice =
        parseFloat(item.sell_price) || parseFloat(item.price) || 0;
      const nextQty = (parseFloat(item.quantity) || 0) + qtyToAdd;
      return {
        ...item,
        quantity: nextQty,
        available_stock: item.available_stock || available,
        total_price: parseFloat((nextQty * sellPrice).toFixed(2)),
      };
    });
  }

  const sellPrice = parseFloat(medicine.sell_price) || 0;
  const nextQty = qtyToAdd;
  return [
    ...items,
    {
      stock_medicine: medicine.id,
      name: medicine.medicine_display,
      barcode: medicine.barcode,
      quantity: nextQty,
      sell_price: sellPrice,
      price: sellPrice,
      total_price: parseFloat((nextQty * sellPrice).toFixed(2)),
      available_stock: available,
      dosage: 1,
      notation: "",
      is_before: "after",
      units_per_meal: 1,
    },
  ];
};

export const addOrIncrementMedicineItem = (items, medicine, quantity = 1) => {
  if (!medicine) return items;
  const qtyToAdd = Number(quantity) || 1;
  const existingIndex = items.findIndex((item) => item.medicine === medicine.id);

  if (existingIndex >= 0) {
    return items.map((item, index) => {
      if (index !== existingIndex) return item;
      const currentQty = parseFloat(item.quantity) || 0;
      const nextQty = currentQty + qtyToAdd;
      const purchasePrice = parseFloat(item.purchase_price) || 0;
      return {
        ...item,
        quantity: nextQty,
        subtotal: nextQty * purchasePrice,
      };
    });
  }

  return [
    ...items,
    {
      medicine: medicine.id,
      name: medicine.name,
      barcode: medicine.barcode,
      quantity: qtyToAdd,
      purchase_price: "",
      government_price: "",
      sell_price: "",
      expiry_date: "",
      subtotal: 0,
    },
  ];
};

export const sumItemTotals = (items) =>
  parseFloat(
    items
      .reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0)
      .toFixed(2),
  );
